/**
 * Mobile Navigation Drawer Controller
 * Handles opening/closing of mobile navigation drawer
 */

const MobileNav = {
  overlay: null,
  drawer: null,
  toggleButton: null,
  closeButton: null,
  focusableElements: null,
  firstFocusableElement: null,
  lastFocusableElement: null,
  
  init() {
    this.overlay = document.getElementById('mobile-nav-overlay');
    this.drawer = document.getElementById('mobile-nav');
    this.toggleButton = document.getElementById('mobile-menu-toggle');
    this.closeButton = document.getElementById('mobile-nav-close');
    
    if (!this.overlay || !this.drawer || !this.toggleButton) {
      return; // Elements not found, exit early
    }
    
    // Get focusable elements
    this.updateFocusableElements();
    
    // Event listeners
    this.toggleButton.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    
    // Keyboard navigation
    this.drawer.addEventListener('keydown', (e) => this.handleKeyDown(e));
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    // Close drawer when clicking a link (mobile navigation)
    const navLinks = this.drawer.querySelectorAll('.mobile-nav__link');
    navLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Small delay to allow navigation
        setTimeout(() => this.close(), 100);
      });
    });
  },
  
  updateFocusableElements() {
    if (!this.drawer) return;
    
    // Get all focusable elements within drawer
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    this.focusableElements = Array.from(this.drawer.querySelectorAll(focusableSelectors));
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  },
  
  handleKeyDown(e) {
    if (!this.isOpen()) return;
    
    // Tab key - trap focus
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement?.focus();
        }
      }
    }
  },
  
  open() {
    if (!this.overlay || !this.drawer) return;
    
    this.updateFocusableElements();
    this.overlay.classList.add('is-open');
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    
    // Focus first focusable element in drawer for accessibility
    this.firstFocusableElement?.focus();
  },
  
  close() {
    if (!this.overlay || !this.drawer) return;
    
    this.overlay.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    
    // Return focus to toggle button
    this.toggleButton?.focus();
  },
  
  isOpen() {
    return this.drawer?.classList.contains('is-open') || false;
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => MobileNav.init());
} else {
  MobileNav.init();
}

